import type { RadioStation } from "@agency/shared";
import { net } from "../net/NetClient.js";
import { store } from "../store/GameStore.js";
import { RadioDirectoryClient } from "./RadioDirectoryClient.js";
import { RadioPlayer } from "./RadioPlayer.js";

/** How long to wait for the server to confirm a tune before trusting its state again. */
const PENDING_TIMEOUT_MS = 25_000;

/**
 * Glue between the local player, the store and the server. Radio commands
 * start playback inside the click (keeping the user gesture), then ask the
 * server, whose state stays authoritative.
 */
class RadioController {
  readonly directory = new RadioDirectoryClient();
  readonly player = new RadioPlayer((stationId) => void this.directory.reportPlay(stationId));
  /** Station this client asked for that the server hasn't confirmed yet. */
  pendingId: string | null = null;
  private pendingTimer?: ReturnType<typeof setTimeout>;
  private readonly listeners = new Set<() => void>();

  mount(): void {
    store.on("radio", (radio) => {
      if (this.pendingId && radio.playing && radio.stationId === this.pendingId) {
        this.setPending(null);
      }
      this.player.sync(radio);
    });
    store.on("rejected", (rejected) => {
      if (rejected.action !== "radioTune" && rejected.action !== "radioStop") return;
      this.setPending(null);
      this.player.sync(store.radio);
    });
    store.on("radioGain", (gain) => this.player.setGain(gain));
    store.on("connection", ({ state }) => {
      if (state !== "closed") return;
      this.setPending(null);
      this.player.stop();
    });
  }

  /** Tune everyone in to a station from this client's list. */
  tune(station: Pick<RadioStation, "id" | "name" | "streamUrl">): void {
    // Already on air: the server would ignore it, so just make sure it plays here.
    if (store.radio.playing && store.radio.stationId === station.id) {
      this.resume();
      return;
    }
    this.player.selectAndPlay(station, { initiatedLocally: true });
    this.setPending(station.id);
    net.send("radioTune", { stationId: station.id });
  }

  /** Replay the last station after a stop. */
  replay(): void {
    const { stationId, stationName, streamUrl } = store.radio;
    if (stationId) this.tune({ id: stationId, name: stationName, streamUrl });
  }

  stopForEveryone(): void {
    this.setPending(null);
    this.player.stop();
    net.send("radioStop", {});
  }

  /** Local recovery (user click): after an error or a blocked autoplay. */
  resume(): void {
    const { status } = this.player.getState();
    if (status === "idle") this.player.sync(store.radio);
    else if (status === "error" || status === "blocked") this.player.retry();
  }

  onPendingChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setPending(stationId: string | null): void {
    clearTimeout(this.pendingTimer);
    this.pendingTimer = undefined;
    if (stationId) {
      this.pendingTimer = setTimeout(() => {
        // The server dropped or never answered our tune: follow its state.
        this.setPending(null);
        this.player.sync(store.radio);
      }, PENDING_TIMEOUT_MS);
    }
    if (this.pendingId === stationId) return;
    this.pendingId = stationId;
    for (const listener of this.listeners) listener();
  }
}

export const radio = new RadioController();
